import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import Input from '@lib/inputs/Input';
import Dropdown from '@lib/inputs/Dropdown';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { useDebounce } from '@containers/common/useDebounce';
import { STATES_TYPE } from '@constants/configs/states';
import { GridStyledWrapper } from '../voterRegistration.styled';
import { VoterRegistrationFormError } from '../VoterRegistrationFormError';
import useVoterRegistration from '../useVoterRegistration';
import { ADDRESS_TYPE, VALIDATIONS } from '../constants/voterRegistrationConstants';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';

interface AddressFieldsProps {
    addressType: ADDRESS_TYPE;
}

function AddressFields({ addressType }: AddressFieldsProps) {
    const intl = useIntl();
    const {
        watch,
        formState: { errors },
        setValue,
    } = useFormContext<IVoterRegistrationFormValues>();

    const state = watch(`${addressType}.state`);
    const zipCode = watch(`${addressType}.zipCode`);
    const county = watch(`${addressType}.county`);
    const debounceZipCode: string = useDebounce(zipCode, 500);

    const { VOTER_REGISTRATION_CHOICES, DROPDOWN_DEFAULT_OPTION, getCounties } = useVoterRegistration();
    const [countyOptions, setCountyOptions] = useState([DROPDOWN_DEFAULT_OPTION]);

    const zipError = errors[addressType]?.zipCode?.message;
    const stateError = errors[addressType]?.state?.message;

    useEffect(() => {
        //Don't call County API for Mailing Address
        if (addressType === ADDRESS_TYPE.MAILING) return;
        if (state && zipCode && zipCode.length === VALIDATIONS.ZIP.max && !zipError && !stateError) {
            const fetchCounties = async () => {
                try {
                    const counties: Record<string, string>[] = await getCounties(state as STATES_TYPE, zipCode);
                    if (counties) {
                        const formattedCounties = counties.map(obj => {
                            const [_value, text] = Object.entries(obj)[0];
                            return { text, value: text };
                        });

                        formattedCounties.unshift(DROPDOWN_DEFAULT_OPTION);
                        setCountyOptions(formattedCounties);
                    }
                } catch (error) {
                    setCountyOptions([DROPDOWN_DEFAULT_OPTION]);
                    setValue(`${addressType}.county`, DROPDOWN_DEFAULT_OPTION.value);
                }
            };
            fetchCounties();
        } else {
            setCountyOptions([DROPDOWN_DEFAULT_OPTION]);
            setValue(`${addressType}.county`, DROPDOWN_DEFAULT_OPTION.value);
        }
    }, [state, zipCode, zipError, stateError, intl]);

    const handleFieldChange = (name: string, value: any) => {
        setValue(name as keyof IVoterRegistrationFormValues, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const handleZipCodeChange = (newZipCode: string) => {
        const formattedZipCode =
            addressType === ADDRESS_TYPE.MAILING &&
            newZipCode.length === VALIDATIONS.ZIP.max + 1 &&
            newZipCode[VALIDATIONS.ZIP.max] !== '-'
                ? `${newZipCode.slice(0, VALIDATIONS.ZIP.max)}-${newZipCode[VALIDATIONS.ZIP.max]}`
                : newZipCode;

        handleFieldChange(`${addressType}.zipCode`, formattedZipCode);
        setValue(`${addressType}.county`, DROPDOWN_DEFAULT_OPTION.value);
    };

    return (
        <GridStyledWrapper>
            <div className="fullWidth">
                <Input
                    label={{
                        text: intl.formatMessage({ id: 'voterRegistration.form.address.streetAddress' }),
                    }}
                    inputData={{
                        id: `${addressType}.addressLine`,
                        name: `${addressType}.addressLine`,
                        isInputRequired: true,
                        value: watch(`${addressType}.addressLine`),
                    }}
                    inputActions={{
                        handleFieldChange: val => handleFieldChange(`${addressType}.addressLine`, val),
                    }}
                />
                <VoterRegistrationFormError
                    name={`${addressType}.addressLine`}
                    hasError={!!errors[addressType]?.addressLine}
                    messageId={errors[addressType]?.addressLine?.message}
                />
            </div>

            <ConditionalRendering isTrue={addressType === ADDRESS_TYPE.RESIDENTIAL}>

                <div className="fullWidth">
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.address.streetAddress2' }),
                        }}
                        inputData={{
                            id: `${addressType}.addressLine2`,
                            name: `${addressType}.addressLine2`,
                            isInputRequired: false,
                            value: watch(`${addressType}.addressLine2`),
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange(`${addressType}.addressLine2`, val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name={`${addressType}.addressLine2`}
                        hasError={!!errors[addressType]?.addressLine2}
                        messageId={errors[addressType]?.addressLine2?.message}
                    />
                </div>

                <div>
                    <Dropdown
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.address.unitType' }),
                        }}
                        inputData={{
                            id: `${addressType}.unitType`,
                            name: `${addressType}.unitType`,
                            isInputRequired: false,
                            value: watch(`${addressType}.unitType`),
                            options: VOTER_REGISTRATION_CHOICES.UNIT_TYPES,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange(`${addressType}.unitType`, val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name={`${addressType}.unitType`}
                        hasError={!!errors[addressType]?.unitType}
                        messageId={errors[addressType]?.unitType?.message}
                    />
                </div>

                <div>
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.address.unitNumber' }),
                        }}
                        inputData={{
                            id: `${addressType}.unitNo`,
                            name: `${addressType}.unitNo`,
                            isInputRequired: false,
                            value: watch(`${addressType}.unitNo`),
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange(`${addressType}.unitNo`, val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name={`${addressType}.unitNo`}
                        hasError={!!errors[addressType]?.unitNo}
                        messageId={errors[addressType]?.unitNo?.message}
                    />
                </div>
            </ConditionalRendering>

            <div>
                <Input
                    label={{
                        text: intl.formatMessage({ id: 'voterRegistration.form.address.city' }),
                    }}
                    inputData={{
                        id: `${addressType}.city`,
                        name: `${addressType}.city`,
                        isInputRequired: true,
                        value: watch(`${addressType}.city`),
                    }}
                    inputActions={{
                        handleFieldChange: val => handleFieldChange(`${addressType}.city`, val),
                    }}
                />
                <VoterRegistrationFormError
                    name={`${addressType}.city`}
                    hasError={!!errors[addressType]?.city}
                    messageId={errors[addressType]?.city?.message}
                />
            </div>

            <div>
                <Dropdown
                    label={{
                        text: intl.formatMessage({ id: 'voterRegistration.form.address.state' }),
                    }}
                    inputData={{
                        id: `${addressType}.state`,
                        name: `${addressType}.state`,
                        isInputRequired: true,
                        value: state,
                        options: VOTER_REGISTRATION_CHOICES.US_STATES,
                        disabled: addressType === 'residentialAddress',
                    }}
                    inputActions={{
                        handleFieldChange: val => handleFieldChange(`${addressType}.state`, val),
                    }}
                />
                <VoterRegistrationFormError
                    name={`${addressType}.state`}
                    hasError={!!errors[addressType]?.state}
                    messageId={errors[addressType]?.state?.message}
                />
            </div>

            <div>
                <Input
                    label={{
                        text: intl.formatMessage({ id: 'voterRegistration.form.address.zip' }),
                    }}
                    inputData={{
                        id: `${addressType}.zipCode`,
                        name: `${addressType}.zipCode`,
                        isInputRequired: true,
                        value: zipCode,
                    }}
                    inputActions={{
                        handleFieldChange: handleZipCodeChange,
                    }}
                />
                <VoterRegistrationFormError
                    name={`${addressType}.zipCode`}
                    hasError={!!errors[addressType]?.zipCode}
                    messageId={errors[addressType]?.zipCode?.message}
                />
            </div>

            <ConditionalRendering isTrue={addressType === ADDRESS_TYPE.RESIDENTIAL}>
                <div>
                    <Dropdown
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.address.county' }),
                        }}
                        inputData={{
                            id: `${addressType}.county`,
                            name: `${addressType}.county`,
                            isInputRequired: true,
                            value: county,
                            options: countyOptions,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange(`${addressType}.county`, val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name={`${addressType}.county`}
                        hasError={!!errors[addressType]?.county}
                        messageId={errors[addressType]?.county?.message}
                    />
                </div>
            </ConditionalRendering>
        </GridStyledWrapper>
    );
}

export default AddressFields;
